// Round action button used inside DesktopTopBar and the AppBar.
// Optionally renders a small badge / dot (notifications), and
// supports a focus ring for keyboard navigation.
//
// `forwardRef` so the inner DOM element can be referenced from a
// parent — notably when this component is passed as the `trigger` of
// a Radix Popover via `PopoverTrigger asChild` (the notification bell
// in AppShell.tsx). Without a forwarded ref the Slot clones our
// element with a ref attached, but the ref is silently dropped at
// this component's boundary, so floating-UI's anchor never wires up
// and the popover content renders off-screen / hidden even though
// the click handler does fire.

import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type TopBarActionProps = {
  icon: LucideIcon;
  label: string;
  badge?: ReactNode;
  badgeKind?: "primary" | "accent";
  onClick?: () => void;
  as?: "button" | "a";
};

export const TopBarAction = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  TopBarActionProps
>(function TopBarAction(
  { icon: Icon, label, badge, badgeKind = "primary", onClick, as: As = "button" },
  forwardedRef,
) {
  const className = cn(
    "relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-foreground/85 transition-colors hover:bg-secondary active:bg-secondary/70",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
  );
  const badgeNode =
    badge !== undefined && badge !== null ? (
      <span
        aria-label={typeof badge === "string" ? `${badge} 個` : undefined}
        className={cn(
          "absolute right-1 top-1 grid min-h-[16px] min-w-[16px] place-items-center rounded-full px-1 text-[9px] font-semibold leading-none ring-2 ring-background",
          badgeKind === "accent"
            ? "bg-accent text-accent-foreground"
            : "bg-primary text-primary-foreground",
        )}
      >
        {badge}
      </span>
    ) : null;
  // `<a>` doesn't accept `type="button"`, so we only add that prop on
  // the default `<button>` branch.
  if (As === "a") {
    return (
      <As
        ref={forwardedRef as React.Ref<HTMLAnchorElement>}
        onClick={onClick}
        aria-label={label}
        title={label}
        className={className}
      >
        <Icon className="h-5 w-5" aria-hidden />
        {badgeNode}
      </As>
    );
  }
  return (
    <button
      ref={forwardedRef as React.Ref<HTMLButtonElement>}
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={className}
    >
      <Icon className="h-5 w-5" aria-hidden />
      {badgeNode}
    </button>
  );
});
